import { useCallback, useEffect, useRef, useState } from 'react';
import SDK from '../SDK';
import Spinner from '../WebChat/Common/Spinner/index';
import logger from '../../Helpers/logger';
import EmptyViewersState from './EmptyViewersState';
import ViewerList from './ViewerList';
import ViewersHeader from './ViewersHeader';

const StoryViewersModal = ({ viewersList, viewersFetching, onClose, storyId, initialPagination }) => {
    const [viewers, setViewers] = useState(viewersList);
    const [nextPage, setNextPage] = useState((initialPagination?.currentPage ?? 0) + 1);
    const [hasMore, setHasMore] = useState(
        (initialPagination?.currentPage ?? 0) + 1 < (initialPagination?.totalPages ?? 1),
    );
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const sentinelRef = useRef(null);

    // Re-sync when parent provides fresh page-0 viewers (story change)
    useEffect(() => {
        setViewers(viewersList);
        const startPage = (initialPagination?.currentPage ?? 0) + 1;
        setNextPage(startPage);
        setHasMore(startPage < (initialPagination?.totalPages ?? 1));
    }, [viewersList, initialPagination]);

    const fetchMore = useCallback(async () => {
        if (isFetchingMore || !hasMore || !storyId) return;
        setIsFetchingMore(true);
        try {
            const res = await SDK.getStoryViewerList(storyId, nextPage);
            if (res?.status === 200 || res?.statusCode === 200) {
                const { viewers: newViewers, currentPage, totalPages } = res.data;
                setViewers(prev => [...prev, ...(newViewers || [])]);
                const fetchedPage = currentPage ?? nextPage;
                setNextPage(fetchedPage + 1);
                setHasMore(fetchedPage + 1 < totalPages);
            }
        } catch (err) {
            logger.error('Failed to fetch more viewers:', err);
        } finally {
            setIsFetchingMore(false);
        }
    }, [isFetchingMore, hasMore, storyId, nextPage]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) fetchMore();
            },
            { threshold: 1.0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [fetchMore]);

    const hasViewers = viewers.length > 0;

    return (
        <div className="story-viewers-popup-overlay" onClick={onClose}>
            <div
                className={`story-viewers-popup-content ${!hasViewers ? 'show_gradient' : ''}`}
                onClick={e => e.stopPropagation()}
            >
                <ViewersHeader hasBorder={hasViewers} onClose={onClose} />

                <div className="viewers-list">
                    {viewersFetching ? (
                        <Spinner />
                    ) : hasViewers ? (
                        <>
                            <ViewerList viewersList={viewers} />
                            <div ref={sentinelRef} className="viewers-scroll-sentinel" />
                            {isFetchingMore && <Spinner />}
                        </>
                    ) : (
                        <EmptyViewersState />
                    )}
                </div>
            </div>
        </div>
    );
};

export default StoryViewersModal;
